/** Minimal promise wrapper over IndexedDB for saves and replays. */
const DB_NAME = 'empires-ascendant';
const DB_VERSION = 1;
export type StoreName = 'saves' | 'replays';

export interface StoredRecord {
  name: string;
  date: number;
  meta: Record<string, unknown>;
  data: string;
}

let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbp) return dbp;
  dbp = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of ['saves', 'replays']) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s, { keyPath: 'name' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open database'));
  });
  return dbp;
}

function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const putRecord = (store: StoreName, r: StoredRecord) => tx(store, 'readwrite', (s) => s.put(r));
export const getRecord = (store: StoreName, name: string) => tx<StoredRecord | undefined>(store, 'readonly', (s) => s.get(name));
export const deleteRecord = (store: StoreName, name: string) => tx(store, 'readwrite', (s) => s.delete(name));
export const listRecords = (store: StoreName) => tx<StoredRecord[]>(store, 'readonly', (s) => s.getAll()).then((l) => l.sort((a, b) => b.date - a.date));
